import React, { useState, useEffect } from "react";
import { Container, Row, Col, ButtonGroup, Button, Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label, Input } from "reactstrap";
import axiosInstance from "../../helpers/axiosInstance";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import MiniWidgets from "./MiniWidgets";
import RevenueAnalytics from "./RevenueAnalytics";
import SalesAnalytics from "./SalesAnalytics";
import { Link, useNavigate } from "react-router-dom";

// New OrderStatusOverview component
const OrderStatusOverview = () => {
  const [orderTimeframe, setOrderTimeframe] = useState("all");
  const [overviewData, setOverviewData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    onGetOverviewData();
  }, [orderTimeframe]);

  const onGetOverviewData = async () => {
    try {
      const response = await axiosInstance.get(
        `api/v1/admin/overview-count`,
        { params: { timeframe: orderTimeframe } }
      );
      setOverviewData(response.data.stats.orders);
    } catch (err) {
      console.error("Failed to fetch overview data", err);
    }
  };

  const getOrderStats = () => {
    if (!overviewData) return {};

    const { byStatus } = overviewData;
    return {
      pending: byStatus.pending,
      accepted: byStatus.accepted,
      printed: byStatus.printed,
      dispatch: byStatus.outForDelivery,
      delivered: byStatus.delivered,
      rejected: byStatus.rejected,
      paymentpending: byStatus.paymentpending,
      cancelled: byStatus.cancelled,
    };
  };

  return (
    <Row className="mb-4">
      <Col lg={12}>
        <div className="card">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h4 className="card-title mb-0">
                Order Status Overview
              </h4>
              <ButtonGroup size="sm">
                <Button
                  color={orderTimeframe === "all" ? "primary" : "light"}
                  onClick={() => setOrderTimeframe("all")}
                >
                  All
                </Button>
                <Button
                  color={orderTimeframe === "today" ? "primary" : "light"}
                  onClick={() => setOrderTimeframe("today")}
                >
                  Today
                </Button>
                <Button
                  color={orderTimeframe === "yesterday" ? "primary" : "light"}
                  onClick={() => setOrderTimeframe("yesterday")}
                >
                  Yesterday
                </Button>
                <Button style={{ color: "black" }} color="light">
                  <Link target="_blank" style={{ color: "black" }} to="/ecommerce-orders">Custom Date</Link>
                </Button>
              </ButtonGroup>
            </div>
            <Row md={24}>
              {Object.entries(getOrderStats()).map(
                ([status, count]) => (
                  <Col md={3} key={status}>
                    <div
                      style={{ cursor: "pointer" }}
                      onClick={() => {
                        navigate(
                          `/ecommerce-orders?status=${status === "dispatch" ? "outfordelivery" : status}&date=${orderTimeframe}`
                        )
                      }}
                      className="text-center bg-red-400 mt-4"
                    >
                      <h5 className="">{count}</h5>
                      <p className="text-muted text-truncate mb-0">
                        {status === "paymentpending" ? "Payment Pending" : status === "dispatch" ? "Dispatched" : status.charAt(0).toUpperCase() +
                          status.slice(1)}
                      </p>
                    </div>
                  </Col>
                )
              )}
            </Row>
          </div>
        </div>
      </Col>
    </Row>
  );
};

const Dashboard = () => {
  const [breadcrumbItems] = useState([
    { title: "Toy Hub Admin", link: "/" },
    { title: "Dashboard", link: "#" },
  ]);

  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboardTimeframe, setDashboardTimeframe] = useState("today");
  const [userRole, setUserRole] = useState(null);

  // New state for custom date modal
  const [isCustomDateModalOpen, setIsCustomDateModalOpen] = useState(false);
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");

  // Fetch user role from localStorage
  useEffect(() => {
    const userString = localStorage.getItem("user");
    if (userString) {
      try {
        const user = JSON.parse(userString);
        const role = user?.role || user?.data?.role;
        setUserRole(role);
      } catch (error) {
        console.error("Dashboard: Failed to parse user from localStorage", error);
        setUserRole(null);
      }
    } else {
      setUserRole(null);
    }
  }, []);

  useEffect(() => {
    if (userRole !== null) {
      fetchDashboardData();
    }
  }, [dashboardTimeframe, userRole, customStartDate, customEndDate]);

  const fetchDashboardData = async () => {
    try {
      setLoading(dashboardData ? false : true);
      const params = { timeframe: dashboardTimeframe };
      if (dashboardTimeframe === 'custom') {
        params.startDate = customStartDate;
        params.endDate = customEndDate;
      }
      const response = await axiosInstance.get(`api/v1/admin/dashboard`, { params });
      setDashboardData(response.data.stats);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to fetch dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const handleCustomDateSubmit = () => {
    if (customStartDate && customEndDate) {
      const start = new Date(customStartDate);
      const end = new Date(customEndDate);
      if (start > end) {
        alert("Start date must be before or equal to end date");
        return;
      }
      setDashboardTimeframe('custom');
      setIsCustomDateModalOpen(false);
      fetchDashboardData();
    }
  };

  const getReports = () => {
    if (!dashboardData) return [];
    let reports = [
      {
        icon: "ri-team-line",
        title: "Total Users",
        value: dashboardData.users.total,
        rate: "",
        desc: "Registered users",
      },
      {
        icon: "ri-store-2-line",
        title: "Total Orders",
        value: dashboardData.orders.total,
        rate: "",
        desc: `${dashboardData.orders.byStatus.pending} pending orders`,
      },
      {
        icon: "ri-shopping-cart-line",
        title: "Total Products",
        value: dashboardData.products.total,
        rate: "",
        desc: `${dashboardData.products.lowStock} low stock items`,
      },
      {
        icon: "ri-truck-line",
        title: "Total Delivery Charges",
        value: dashboardData.orders.totalDeliveryCharges,
        rate: "",
        desc: "Overall delivery charges",
      },
      {
        icon: "ri-currency-line",
        title: "Total Revenue",
        value: dashboardData.orders.totalRevenue,
        rate: "",
        desc: "Overall earnings",
      },
      {
        icon: "ri-currency-line",
        title: "Total Revenue COD",
        value: dashboardData.orders.totalRevenueCod,
        rate: "",
        desc: "Overall earnings",
      },
      {
        icon: "ri-currency-line",
        title: "Total Revenue DTDC",
        value: dashboardData.orders.totalRevenueDTDC,
        rate: "",
        desc: "Overall earnings",
        path: "/reports/dtdc-cod",
      },
    ];

    // Filter reports based on user role
    if (userRole === 'sub-admin') {
      reports = reports.filter(report =>
        report.title !== "Total Revenue" &&
        report.title !== "Total Revenue COD" &&
        report.title !== "Total Revenue DTDC"
      );
    }

    return reports;
  };

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        </Container>
      </div>
    );
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Dashboard" breadcrumbItems={breadcrumbItems} />

          <Row className="mb-3">
            <Col className="d-flex justify-content-end">
              <ButtonGroup>
                <Button
                  color={dashboardTimeframe === "today" ? "primary" : "light"}
                  onClick={() => setDashboardTimeframe("today")}
                >
                  Today
                </Button>
                <Button
                  color={dashboardTimeframe === "yesterday" ? "primary" : "light"}
                  onClick={() => setDashboardTimeframe("yesterday")}
                >
                  Yesterday
                </Button>
                <Button
                  color={dashboardTimeframe === "week" ? "primary" : "light"}
                  onClick={() => setDashboardTimeframe("week")}
                >
                  1 Week
                </Button>
                <Button
                  color={dashboardTimeframe === "month" ? "primary" : "light"}
                  onClick={() => setDashboardTimeframe("month")}
                >
                  1 Month
                </Button>
                <Button
                  color={dashboardTimeframe === "year" ? "primary" : "light"}
                  onClick={() => setDashboardTimeframe("year")}
                >
                  1 Year
                </Button>
                <Button
                  color={dashboardTimeframe === "all" ? "primary" : "light"}
                  onClick={() => setDashboardTimeframe("all")}
                >
                  All Time
                </Button>
                <Button
                  color={dashboardTimeframe === "custom" ? "primary" : "light"}
                  onClick={() => setIsCustomDateModalOpen(true)}
                >
                  Custom
                </Button>
              </ButtonGroup>
            </Col>
          </Row>

          {/* Custom Date Modal */}
          <Modal
            isOpen={isCustomDateModalOpen}
            toggle={() => setIsCustomDateModalOpen(!isCustomDateModalOpen)}
          >
            <ModalHeader toggle={() => setIsCustomDateModalOpen(false)}>
              Select Custom Date Range
            </ModalHeader>
            <ModalBody>
              <Form>
                <FormGroup>
                  <Label for="startDate">Start Date</Label>
                  <Input
                    type="date"
                    name="startDate"
                    id="startDate"
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                  />
                </FormGroup>
                <FormGroup>
                  <Label for="endDate">End Date</Label>
                  <Input
                    type="date"
                    name="endDate"
                    id="endDate"
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                  />
                </FormGroup>
              </Form>
            </ModalBody>
            <ModalFooter>
              <Button color="primary" onClick={handleCustomDateSubmit}>
                Apply
              </Button>{' '}
              <Button color="secondary" onClick={() => setIsCustomDateModalOpen(false)}>
                Cancel
              </Button>
            </ModalFooter>
          </Modal>

          <Row>
            <Col xl={12}>
              <Row>
                <MiniWidgets reports={getReports()} />
              </Row>
            </Col>

            <Col xl={12}>
              <OrderStatusOverview />
            </Col>

            <Col xl={8}>
              <RevenueAnalytics />
            </Col>

            <Col xl={4}>
              <SalesAnalytics />
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default Dashboard;
